import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";

function findBadgeHolderByName(badgeHolders, fullName) {
  const normalized = (fullName || "").trim();
  if (!normalized) return null;
  return (badgeHolders || []).find((bh) => {
    const badgeHolderFullName = `${bh.first_name || ""} ${bh.last_name || ""}`.trim();
    return badgeHolderFullName === normalized;
  });
}

function resolveInspectorImage(inspector, badgeHolder, employeeAvatar) {
  const fromInspector = inspector?.avatar ?? inspector?.img;
  if (fromInspector) return fromInspector;

  if (badgeHolder) {
    const fromBadgeHolder = badgeHolder.img ?? badgeHolder.avatar;
    if (fromBadgeHolder) return fromBadgeHolder;
    return "";
  }

  return employeeAvatar || "";
}

function normalizeInspectorName(person) {
  const fullName = (
    person?.full_name ||
    person?.name ||
    `${person?.first_name || ""} ${person?.last_name || ""}`
  ).trim();
  const nameParts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ") || "",
    fullName,
  };
}

function mapPersonToAvailableInspector(
  person,
  { badgeHolders = [], employeeAvatar = null, defaultBadgeLevel = null } = {}
) {
  const { firstName, lastName, fullName } = normalizeInspectorName(person);
  const badgeHolder = findBadgeHolderByName(badgeHolders, fullName);
  const img = resolveInspectorImage(person, badgeHolder, employeeAvatar);

  return {
    id: person?.id,
    badgeLevel:
      person?.level || person?.badge_level || badgeHolder?.badge_level || defaultBadgeLevel || null,
    firstName,
    lastName,
    fullName,
    title: badgeHolder?.title || person?.title || null,
    architect_will_coach:
      badgeHolder?.architect_will_coach ??
      badgeHolder?.master_will_coach ??
      person?.architect_will_coach ??
      person?.master_will_coach ??
      null,
    img,
    isHoverEnabled: !!(img || firstName || lastName),
  };
}

function isCoachOrArchitectBadgeLevel(level) {
  const normalized = normalizeBadgeLevel(level);
  return normalized === "coach" || normalized === "architect";
}

function collectInspectorSources(item) {
  const legacy = item?.available_inspectors || [];
  const coaches = (item?.coaches || []).map((coach) => ({
    ...coach,
    level: coach?.level || coach?.badge_level || "Coach",
  }));
  const architects = (item?.architects ?? item?.masters ?? []).map((architect) => ({
    ...architect,
    level: architect?.level || architect?.badge_level || "Master",
  }));
  const badgeHolders = (item?.badge_holders || [])
    .filter((holder) => isCoachOrArchitectBadgeLevel(holder?.badge_level || holder?.level))
    .map((holder) => ({
      ...holder,
      level: holder?.level || holder?.badge_level || null,
    }));

  const seen = new Set();
  const merged = [];

  [...legacy, ...coaches, ...architects, ...badgeHolders].forEach((person) => {
    if (!person) return;
    const { fullName } = normalizeInspectorName(person);
    const idKey = person.id != null ? `id:${person.id}` : null;
    const nameKey = fullName ? `name:${fullName.toLowerCase()}` : null;
    if ((idKey && seen.has(idKey)) || (nameKey && seen.has(nameKey))) return;
    if (idKey) seen.add(idKey);
    if (nameKey) seen.add(nameKey);
    merged.push(person);
  });

  return merged;
}

/**
 * Maps assignment API rows to PeopleAvatarsButton inspector items.
 * Supports legacy `available_inspectors`, `coaches` / `architects` (legacy `masters`), and Coach/Architect `badge_holders`.
 */
export function buildAvailableInspectorsFromAssignment(item) {
  const badgeHolders = item?.badge_holders || [];
  const employeeAvatar = item?.employee_avatar || null;

  return collectInspectorSources(item).map((person) =>
    mapPersonToAvailableInspector(person, {
      badgeHolders,
      employeeAvatar,
      defaultBadgeLevel: person?.level || person?.badge_level || null,
    })
  );
}

export {
  findBadgeHolderByName,
  resolveInspectorImage,
  normalizeInspectorName,
  mapPersonToAvailableInspector,
  collectInspectorSources,
};
